/* CustomizeContext — single source of truth for app branding + labels.
 * - GETs /api/customize once at app boot, refreshes on demand.
 * - Exposes { settings, reload, label(key, fallback) } via hook.
 * - Applies primary/secondary colour as CSS variables + sets document
 *   title to match the configured app_name.
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const CustomizeContext = createContext(null);

export function CustomizeProvider({ children }) {
  const [settings, setSettings] = useState(null);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/customize");
      setSettings(data);
    } catch {
      // Unauthenticated calls fail silently — provider will retry on
      // next login when AuthContext mounts again.
      setSettings(null);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Apply branding as CSS variables + document title whenever settings change.
  useEffect(() => {
    if (!settings?.branding) return;
    const b = settings.branding;
    const root = document.documentElement;
    if (b.primary_color) root.style.setProperty("--brand-primary", b.primary_color);
    if (b.secondary_color) root.style.setProperty("--brand-secondary", b.secondary_color);
    if (b.app_name) document.title = b.app_name;
  }, [settings]);

  const label = useCallback((key, fallback = "") => {
    return (settings?.labels && settings.labels[key]) || fallback;
  }, [settings]);

  return (
    <CustomizeContext.Provider value={{ settings, reload, label }}>
      {children}
    </CustomizeContext.Provider>
  );
}

export function useCustomize() {
  const ctx = useContext(CustomizeContext);
  if (!ctx) {
    // Safe no-op fallback if provider hasn't mounted yet.
    return { settings: null, reload: () => Promise.resolve(), label: (_k, f = "") => f };
  }
  return ctx;
}
